import "./FromAndToComponent.less";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { MailboxItem, OfficeWrapper } from "../../../services/OfficeWrapper";
import { FormValidationHelpers } from "../../../helpers/FormValidationHelpers";
import EmailAddressDetails = Office.EmailAddressDetails;
import AssigneeFluent9 from "../assignee/AssigneeFluent9";
import { Logger } from "../../../services/Logger";
import { IProjectsService } from "../../../services/NewformaApi/IProjectsService";
import { TeamMemberNormalized } from "../../../models/projectTeam/TeamViewResponse";
import { EmailRecipients } from "../../../models/shared/FileTransferEmailFieldsDetails";
import { Link, makeStyles, tokens } from "@fluentui/react-components";

interface IPersonaProps {
    text?: string;
    secondaryText?: string;
    data?: {
        nrn?: string;
        email?: string;
        [key: string]: any;
    };
    [key: string]: any;
}

interface ITag {
    key: string;
    name?: string;
}

const useStyles = makeStyles({
    container: {},
    addCcButton: {
        display: "block",
        marginLeft: "auto",
        fontSize: tokens.fontSizeBase200,
    },
});

export interface FromAndToComponentProps extends LocalizeContextProps {
    key?: string;
    className?: string;
    isFromRequired: boolean;
    isToRequired: boolean;
    isCcRequired?: boolean;
    includeCc: boolean;
    officeWrapper: OfficeWrapper;
    onFromChange: (people: IPersonaProps[]) => void;
    onToChange: (people: IPersonaProps[]) => void;
    onCcChange?: (people: IPersonaProps[]) => void;
    disabled: boolean;
    formValidationHelpers: FormValidationHelpers;
    mailboxItem: MailboxItem | null;
    project: ITag | null;
    logger: Logger;
    projectsService: IProjectsService;
    shouldHideFrom: boolean;
    shouldClearEmails?: boolean;
    isTransferFailed?: boolean;
    EmailRecipients?: EmailRecipients;
    isFileTransferComponent?: boolean;
    isForwardInProgress?: boolean;
    onEmailCheck?: (value: boolean) => void;
    from?: IPersonaProps[];
    to?: IPersonaProps[];
    cc?: IPersonaProps[];
}

const FromAndToComponent = (props: FromAndToComponentProps) => {
    const [fromState, setFromState] = useState<IPersonaProps[]>(props.from ?? []);
    const [toState, setToState] = useState<IPersonaProps[]>(props.to ?? []);
    const [ccState, setCcState] = useState<IPersonaProps[]>(props.cc ?? []);

    const fromValues = props.from ?? fromState;
    const toValues = props.to ?? toState;
    const ccValues = props.cc ?? ccState;

    const [showCcField, setShowCcField] = useState<boolean>(ccValues.length > 0);
    const [emailGroups, setEmailGroups] = useState<IPersonaProps[]>([]);

    const styles = useStyles();

    useEffect(() => {
        if (props.from) {
            setFromState(props.from);
        }
    }, [props.from]);

    useEffect(() => {
        if (props.to) {
            setToState(props.to);
        }
    }, [props.to]);

    useEffect(() => {
        if (props.cc) {
            setCcState(props.cc);
        }
    }, [props.cc]);

    const removeNewformaAutoFileEmail = (recipients?: EmailAddressDetails[]): IPersonaProps[] => {
        return (recipients ?? [])
            .map((recipient) => recipient.emailAddress)
            .filter((email) => !email.toLowerCase().endsWith("newforma.email"))
            .map((email) => ({ text: email }));
    };

    const populateFields = (): void => {
        if (props.shouldClearEmails) {
            setFromState([]);
            setToState([]);
            setCcState([]);
            props.onFromChange([]);
            props.onToChange([]);
            if (props.onCcChange) {
                props.onCcChange([]);
            }
            setShowCcField(false);
            return;
        }

        const from = props.officeWrapper.getSenderEmailAddress();
        const to = props.officeWrapper.getCurrentEmailToRecipients();
        const cc = props.officeWrapper.getCurrentEmailCcRecipients();

        const fromAsPersonaProps: IPersonaProps[] =
            props.isForwardInProgress && props.EmailRecipients && props.EmailRecipients.from?.length
                ? props.EmailRecipients.from
                : from
                ? [{ text: from }]
                : [];

        const toAsPersonaProps: IPersonaProps[] = props.isForwardInProgress
            ? props.EmailRecipients?.to ?? []
            : props.isTransferFailed && props.EmailRecipients && props.EmailRecipients.to?.length
            ? props.EmailRecipients.to
            : removeNewformaAutoFileEmail(to);

        const ccAsPersonaProps: IPersonaProps[] = props.isForwardInProgress
            ? props.EmailRecipients?.cc ?? []
            : props.isTransferFailed && props.EmailRecipients && props.EmailRecipients.cc?.length
            ? props.EmailRecipients.cc
            : removeNewformaAutoFileEmail(cc);

        const nextFrom = props.isFileTransferComponent ? removeNewformaAutoFileEmail(to) : fromAsPersonaProps;
        const nextTo = props.isFileTransferComponent && !props.isTransferFailed ? fromAsPersonaProps : toAsPersonaProps;

        props.onFromChange(nextFrom);
        props.onToChange(nextTo);
        if (props.onCcChange) {
            props.onCcChange(ccAsPersonaProps);
        }
        setFromState(nextFrom);
        setToState(nextTo);
        setCcState(ccAsPersonaProps);
        setShowCcField(!!(ccAsPersonaProps.length && props.includeCc));
    };

    // Load email groups for Assignee selectors
    useEffect(() => {
        const projectNrn = props.project?.key;
        if (!projectNrn) {
            setEmailGroups([]);
            if (!props.from && !props.to && !props.cc) {
                populateFields();
            }
            return;
        }

        (async () => {
            try {
                const members: TeamMemberNormalized[] = await props.projectsService.getAllTeamMembersNormalized(
                    projectNrn,
                    ""
                );
                const groups = props.formValidationHelpers.getPersonaPropsFromTeamMembers(
                    members.filter((m) => m.nrn.includes("nrn:teamgroup"))
                );
                setEmailGroups(groups);
                if (!props.from && !props.to && !props.cc) {
                    populateFields();
                }
            } catch (error) {
                props.logger.error("FromAndToComponent failed to load team members", error);
                if (!props.from && !props.to && !props.cc) {
                    populateFields();
                }
            }
        })();
    }, [props.project?.key, props.formValidationHelpers, props.projectsService, props.logger, props.mailboxItem]);

    // Keep Add CC visibility in sync with controlled CC values.
    useEffect(() => {
        if (ccValues.length > 0) {
            setShowCcField(true);
        }
    }, [ccValues.length]);

    useEffect(() => {
        if (props.shouldClearEmails) {
            setShowCcField(false);
        }
    }, [props.shouldClearEmails]);

    const toAndCcEmailDuplicates = useMemo(() => {
        const toEmails = new Set(
            toValues
                .map((item) => item.data?.email ?? item.secondaryText ?? item.text)
                .filter((x): x is string => Boolean(x))
        );
        const ccEmails = new Set(
            ccValues
                .map((item) => item.data?.email ?? item.secondaryText ?? item.text)
                .filter((x): x is string => Boolean(x))
        );

        return Array.from(toEmails).filter((email) => ccEmails.has(email));
    }, [toValues, ccValues]);

    const hasSameToAndCc = toAndCcEmailDuplicates.length > 0;

    useEffect(() => {
        if (props.onEmailCheck) {
            props.onEmailCheck(hasSameToAndCc);
        }
    }, [hasSameToAndCc, props.onEmailCheck]);

    const handleFromChange = (items: IPersonaProps[]): void => {
        setFromState(items);
        props.onFromChange(items);
    };

    const handleToChange = (items: IPersonaProps[]): void => {
        setToState(items);
        props.onToChange(items);
        if (props.onEmailCheck) {
            const toEmails = new Set(
                items
                    .map((item) => item.data?.email ?? item.secondaryText ?? item.text)
                    .filter((x): x is string => Boolean(x))
            );
            const ccEmails = new Set(
                ccValues
                    .map((item) => item.data?.email ?? item.secondaryText ?? item.text)
                    .filter((x): x is string => Boolean(x))
            );
            props.onEmailCheck(Array.from(toEmails).some((email) => ccEmails.has(email)));
        }
    };

    const handleCcChange = (items: IPersonaProps[]): void => {
        setCcState(items);
        if (props.onCcChange) {
            props.onCcChange(items);
        }
        if (props.onEmailCheck) {
            const toEmails = new Set(
                toValues
                    .map((item) => item.data?.email ?? item.secondaryText ?? item.text)
                    .filter((x): x is string => Boolean(x))
            );
            const ccEmails = new Set(
                items
                    .map((item) => item.data?.email ?? item.secondaryText ?? item.text)
                    .filter((x): x is string => Boolean(x))
            );
            props.onEmailCheck(Array.from(toEmails).some((email) => ccEmails.has(email)));
        }
    };

    const handleShowCc = (): void => {
        setShowCcField(true);
    };

    let fromComponent: JSX.Element | null = null;
    if (!props.shouldHideFrom) {
        fromComponent = (
            <AssigneeFluent9
                testid="fromandto-from"
                assignees={props.shouldClearEmails ? [] : fromValues}
                selectedProject={props.project}
                disabled={props.disabled}
                currentUserEmail={props.officeWrapper.userProfileEmailAddress}
                onAssigneeChanged={handleFromChange}
                formValidationHelpers={props.formValidationHelpers}
                logger={props.logger}
                projectsService={props.projectsService}
                label={props.translate("SHARED.FROM_AND_TO.FROM_LABEL") as string}
                required={props.isFromRequired}
                showAssignToMe={props.shouldHideFrom}
                maxAllowed={1}
                sameToAndCcEmail={toAndCcEmailDuplicates}
                panelString={props.key}
                emailGroup={emailGroups}
            />
        );
    }

    const toComponent: JSX.Element = (
        <AssigneeFluent9
            testid="fromandto-to"
            assignees={props.shouldClearEmails ? [] : toValues}
            selectedProject={props.project}
            disabled={props.disabled}
            currentUserEmail={props.officeWrapper.userProfileEmailAddress}
            onAssigneeChanged={handleToChange}
            formValidationHelpers={props.formValidationHelpers}
            logger={props.logger}
            projectsService={props.projectsService}
            label={props.translate("SHARED.FROM_AND_TO.TO_LABEL") as string}
            required={props.isToRequired}
            showAssignToMe={false}
            type="to"
            panelString={props.key}
            sameToAndCcEmail={toAndCcEmailDuplicates}
            emailGroup={emailGroups}
            placeholder={props.translate("PROJECT_TEAM.SELECT_TEAM_MEMBERS") as string}
        />
    );

    let addCcButton: JSX.Element | null = null;
    if (props.includeCc && !showCcField) {
        addCcButton = (
            <Link className={styles.addCcButton} data-testid="add-cc-button" onClick={handleShowCc}>
                {props.translate("SHARED.FROM_AND_TO.ADD_CC") as string}
            </Link>
        );
    }

    let ccFieldComponent: JSX.Element | null = null;
    if (props.includeCc && showCcField) {
        ccFieldComponent = (
            <AssigneeFluent9
                testid="fromandto-cc"
                assignees={props.shouldClearEmails ? [] : ccValues}
                selectedProject={props.project}
                disabled={props.disabled}
                currentUserEmail={props.officeWrapper.userProfileEmailAddress}
                onAssigneeChanged={handleCcChange}
                formValidationHelpers={props.formValidationHelpers}
                logger={props.logger}
                projectsService={props.projectsService}
                label={props.translate("SHARED.FROM_AND_TO.CC_LABEL") as string}
                required={!!props.isCcRequired}
                showAssignToMe={false}
                type="cc"
                sameCcAndTo={hasSameToAndCc}
                panelString={props.key}
                sameToAndCcEmail={toAndCcEmailDuplicates}
                emailGroup={emailGroups}
                placeholder={props.translate("PROJECT_TEAM.SELECT_TEAM_MEMBERS") as string}
            />
        );
    }

    return (
        <div className={styles.container}>
            {fromComponent}
            {toComponent}
            {ccFieldComponent}
            {addCcButton}
        </div>
    );
};

export default withLocalize(FromAndToComponent);
